import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
// Import the RadarChart component from charts directory
import { parseEEATData } from "src/components/dashboard/articleEditor/utils";
import { RadarChart } from "src/components/dashboard/charts";
import { LightBulb } from "src/icons/light-bulb";
import SuggestionsList from "./SuggestionsList";

import { ComparisonIcon } from "src/icons/comparison-icon";

import { MinusOutlined } from "src/icons/minus-outlined";
import DetailedSummary from "./DetailedSummary";

interface ScoreDetailProps {
  label: string;
  original: number;
  updated: number;
  description: string;
}

const ScoreDetail: React.FC<ScoreDetailProps> = ({
  label,
  original,
  updated,
  description,
}) => {
  return (
    <Card
      sx={{
        p: "20px",
        mb: 2,
        borderRadius: "8px",
        backgroundColor: "#F9FAFC",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontSize: "16px",
          fontWeight: "500",
          fontFamily: "Inter",
          color: "#121828",
          mb: 1,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: "400",
          fontFamily: "Inter",
          color: "#6B7280",
          mb: 2,
        }}
      >
        {description || ""}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          gap: "8px",
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          alignSelf="stretch"
          padding="8px 16px"
          gap="8px"
          flex="1 0 0"
          height="70px"
          width="121px"
          sx={{
            borderRadius: "8px",
            background: "#FFFFFF",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              fontFamily: "Inter",
              color: "#7B85AD",
              mr: 1,
            }}
          >
            Original
          </Typography>
          <Box display="flex" alignItems="center" gap="10px">
            <ScoreCircle score={original} size={20} />
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                fontFamily: "Inter",
                color: "#7B85AD",
              }}
            >
              {original?.toFixed(1) ?? "N/A"}
            </Typography>
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          alignSelf="stretch"
          padding="8px 16px"
          gap="8px"
          flex="1 0 0"
          height="70px"
          width="121px"
          sx={{
            borderRadius: "8px",
            background: "#FFFFFF",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              fontFamily: "Inter",
              color: "#7B85AD",
              mr: 1,
            }}
          >
            Updated
          </Typography>
          <Box display="flex" alignItems="center" gap="10px">
            <ScoreCircle score={updated} size={20} />
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                fontFamily: "Inter",
                color: "#121828",
              }}
            >
              {updated?.toFixed(1) ?? "N/A"}
              {updated > original && (
                <span
                  style={{
                    color: "#4caf50",
                    fontSize: "12px",
                    marginLeft: "5px",
                  }}
                >
                  +{(updated - original).toFixed(1)}▲
                </span>
              )}
              {updated < original && (
                <span
                  style={{
                    color: "#F44336",
                    fontSize: "12px",
                    marginLeft: "5px",
                  }}
                >
                  {(updated - original).toFixed(1)}▼
                </span>
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

interface SingleScoreDetailProps {
  label: string;
  score: number;
  assessment: string;
}

const SingleScoreDetail: React.FC<SingleScoreDetailProps> = ({
  label,
  score,
  assessment,
}) => {
  return (
    <Card
      sx={{
        p: "20px",
        mb: 2,
        borderRadius: "8px",
        backgroundColor: "#F9FAFC",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontSize: "16px",
          fontWeight: "500",
          fontFamily: "Inter",
          color: "#121828",
          mb: 1,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: "400",
          fontFamily: "Inter",
          color: "#6B7280",
          mb: 2,
        }}
      >
        {assessment || ""}
      </Typography>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        alignSelf="stretch"
        padding="8px 16px"
        gap="8px"
        sx={{
          borderRadius: "8px",
          background: "#FFFFFF",
          height: "70px",
          width: "50%",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "600",
            fontFamily: "Inter",
            color: "#7B85AD",
          }}
        >
          Score
        </Typography>
        <Box display="flex" alignItems="center" gap="10px">
          <ScoreCircle score={score} size={20} />
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "600",
              fontFamily: "Inter",
              color: "#121828",
            }}
          >
            {score?.toFixed(1) ?? "N/A"}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

interface SidebarProps {
  title?: string;
  recommendedScore?: number;
  isLoading?: boolean;
  evaluationData: {
    articleEEATScore?: {
      analysis?: {
        authoritativeness?: {
          comparison?: string;
          assessment?: string;
        };
        experience?: {
          comparison?: string;
          assessment?: string;
        };
        expertise?: {
          comparison?: string;
          assessment?: string;
        };
        trustworthiness?: {
          comparison?: string;
          assessment?: string;
        };
        summary?: Array<{
          title: string;
          detail: string;
        }>;
      };
      articles?: {
        original?: {
          label?: string;
          overall?: number;
          scores?: {
            authoritativeness?: number;
            experience?: number;
            expertise?: number;
            trustworthiness?: number;
          };
        };
        updated?: {
          label?: string;
          overall?: number;
          scores?: {
            authoritativeness?: number;
            experience?: number;
            expertise?: number;
            trustworthiness?: number;
          };
        };
      };
      article?: {
        scores?: {
          authoritativeness?: number;
          experience?: number;
          expertise?: number;
          trustworthiness?: number;
        };
        overall?: number;
      };
      suggestions?: string[];
      summary?: Array<{
        title: string;
        detail: string;
      }>;
    };
  };
  optimizedMode?: boolean;
  isEEATMode?: boolean;
  onUpdateArticleEEAT?: () => void;
  isEEATLoading?: boolean;
}

// Helper function to determine color based on score
const getScoreColor = (score?: number) => {
  if (score === undefined) return "#cccccc";
  if (score < 4) return "#FF0000"; // Red for 0-3.9
  if (score < 8) return "#f59e0b"; // Orange/Yellow for 4-7.9
  return "#4CAF50"; // Green for 8-10
};

// Circular progress component with score
const ScoreCircle = ({
  score,
  size = 20,
}: {
  score?: number;
  size?: number;
}) => {
  const color = getScoreColor(score);
  const normalizedValue =
    score !== undefined ? Math.min(Math.max(score * 10, 0), 100) : 0;

  return (
    <Box
      position="relative"
      display="inline-flex"
      sx={{ width: size, height: size }}
    >
      <CircularProgress
        variant="determinate"
        value={normalizedValue}
        size={size}
        thickness={6}
        sx={{
          color: color,
          "& .MuiCircularProgress-circle": {
            strokeLinecap: "round",
          },
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: size * 0.7,
            height: size * 0.7,
            borderRadius: "50%",
            backgroundColor: "white",
          }}
        />
      </Box>
    </Box>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  title = "Article EEAT Score",
  recommendedScore = 8.0,
  evaluationData,
  optimizedMode = true,
  isLoading = false,
  onUpdateArticleEEAT,
  isEEATMode = false,
  isEEATLoading = false,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [showDetailedSummary, setShowDetailedSummary] = useState(false);
  const detailedSummaryRef = useRef<HTMLDivElement>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Add effect to scroll to the detailed summary when opened
  useEffect(() => {
    if (showDetailedSummary && detailedSummaryRef.current) {
      setTimeout(() => {
        detailedSummaryRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [showDetailedSummary]);

  // Get the data based on mode
  const rawEeatData = evaluationData?.articleEEATScore;

  // Parse the data
  const parsedData = parseEEATData(rawEeatData);

  // Check if we have valid data
  const hasData = !!parsedData;

  // No need for further parsing since we've already extracted structured data
  const analysis = parsedData?.analysis;
  const articleData = parsedData?.article;
  const summary = parsedData?.analysis?.summary || parsedData?.summary || [];
  // Parse suggestions if it's a string (JSON string)
  const suggestions = (() => {
    const rawSuggestions = parsedData?.suggestions || [];
    if (typeof rawSuggestions === "string") {
      try {
        return JSON.parse(rawSuggestions);
      } catch (error) {
        console.error("Error parsing suggestions:", error);
        return [];
      }
    }
    return rawSuggestions;
  })();

  // For single article mode, use the article data directly
  const singleArticleData = optimizedMode ? null : articleData;

  // Ensure we have the correct data for each mode
  const hasArticleData = optimizedMode
    ? !!(parsedData?.articles?.original || parsedData?.articles?.updated)
    : !!singleArticleData;

  // Check if we have all required data
  const isDataComplete =
    hasData && (optimizedMode ? hasArticleData : hasArticleData);

  // Prepare radar chart data
  let radarData = {
    labels: ["Experience", "Expertise", "Authoritativeness", "Trustworthiness"],
    datasets: [] as any[],
  };

  if (optimizedMode) {
    // Comparison mode
    const articles = parsedData?.articles;
    radarData.datasets = [
      {
        label: "Original",
        data: [
          articles?.original?.scores?.experience ?? 0,
          articles?.original?.scores?.expertise ?? 0,
          articles?.original?.scores?.authoritativeness ?? 0,
          articles?.original?.scores?.trustworthiness ?? 0,
        ],
      },
      {
        label: "Updated",
        data: [
          articles?.updated?.scores?.experience ?? 0,
          articles?.updated?.scores?.expertise ?? 0,
          articles?.updated?.scores?.authoritativeness ?? 0,
          articles?.updated?.scores?.trustworthiness ?? 0,
        ],
      },
    ];
  } else {
    // Single article mode
    radarData.datasets = [
      {
        label: "Score",
        data: [
          singleArticleData?.scores?.experience ?? 0,
          singleArticleData?.scores?.expertise ?? 0,
          singleArticleData?.scores?.authoritativeness ?? 0,
          singleArticleData?.scores?.trustworthiness ?? 0,
        ],
      },
    ];
  }

  return (
    <Box
      sx={{
        height: "calc(100vh - 64px)",
        bgcolor: "#FFF",
        p: "18px",
        borderRadius: 1,
        overflowY: "auto", // Direct scrollbar on the entire sidebar
      }}
    >
      {isLoading ? (
        // Skeleton loader for title and recommendation
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" height={30} width="60%" />
          {optimizedMode && <Skeleton variant="text" height={24} width="80%" />}
        </Box>
      ) : (
        <>
          <Typography
            sx={{
              mb: 1,
              fontWeight: 500,
              fontSize: "20px",
              fontFamily: "Inter",
              color: "#121828",
            }}
          >
            {hasData
              ? title
              : optimizedMode
              ? "EEAT Score Comparison"
              : "EEAT Score Assessment"}
          </Typography>

          {optimizedMode && hasData && (
            <Typography
              sx={{
                mb: 3,
                fontSize: "16px",
                fontWeight: 400,
                fontFamily: "Inter",
                color: "#121828",
              }}
            >
              <strong
                style={{
                  color: "#5048E5",
                  fontWeight: 700,
                  fontSize: "16px",
                  fontFamily: "Inter",
                }}
              >
                Score {recommendedScore}
              </strong>{" "}
              or above is recommended.
            </Typography>
          )}
        </>
      )}

      {isLoading ? (
        // Skeleton loader
        <Box sx={{ p: 2, width: "100%" }}>
          <Skeleton variant="text" height={40} width="70%" />
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="rectangular" height={200} width="100%" />
          </Box>
          <Box sx={{ mt: 3 }}>
            <Skeleton variant="text" height={30} width="80%" />
            <Skeleton variant="text" height={20} width="100%" />
            <Skeleton variant="text" height={20} width="90%" />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" height={30} width="60%" />
            <Skeleton variant="text" height={20} width="100%" />
            <Skeleton variant="text" height={20} width="85%" />
          </Box>
        </Box>
      ) : isDataComplete ? (
        <>
          {/* Radar Chart */}
          <Box
            sx={{
              // mb: "10px",
              position: "relative",
              height: optimizedMode ? "280px" : "280px", // Reduced height when no legend
              mt: optimizedMode ? "-100px" : "-80px", // Negative margin to reduce space at top
            }}
          >
            <RadarChart
              data={radarData}
              showLegend={optimizedMode}
              usePurpleColor={!optimizedMode}
            />
          </Box>

          {/* Overall Score */}
          <Box sx={{ mb: 3, mt: optimizedMode ? "6rem" : "3rem" }}>
            {optimizedMode ? (
              // Comparison mode with Card wrapper
              <Card
                sx={{
                  p: "8px 16px",
                  mb: 2,
                  width: "100%",
                  border: "1px solid #E6E8F0",
                  borderRadius: "8px",
                  height: "126px",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    fontFamily: "Inter",
                    color: "#121828",
                  }}
                >
                  Overall EEAT
                </Typography>
                <Box
                  display="flex"
                  gap="8px"
                  alignItems="flex-start"
                  alignSelf="stretch"
                  width="100%"
                  mt="4px"
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    alignSelf="stretch"
                    padding="8px 16px"
                    gap="8px"
                    flex="1 0 0"
                    height="70px"
                    width="131px"
                    sx={{
                      borderRadius: "8px",
                      background: "#F9FAFC",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "600",
                        fontFamily: "Inter",
                        color: "#7B85AD",
                      }}
                    >
                      Original
                    </Typography>
                    <Box display="flex" flexDirection="row" gap="8px">
                      <Box sx={{ mb: 1 }}>
                        <ScoreCircle
                          score={parsedData?.articles?.original?.overall}
                          size={24}
                        />
                      </Box>

                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: "600",
                          fontFamily: "Inter",
                          color: "#7B85AD",
                          height: "32px",
                        }}
                      >
                        {parsedData?.articles?.original?.overall ?? "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    alignSelf="stretch"
                    padding="8px 16px"
                    gap="8px"
                    flex="1 0 0"
                    height="70px"
                    width="131px"
                    sx={{
                      borderRadius: "8px",
                      background: "#F9FAFC",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "600",
                        fontFamily: "Inter",
                        color: "#7B85AD",
                      }}
                    >
                      Updated
                    </Typography>
                    <Box display="flex" flexDirection="row" gap="8px">
                      <Box sx={{ mb: 1 }}>
                        <ScoreCircle
                          score={parsedData?.articles?.updated?.overall}
                          size={24}
                        />
                      </Box>

                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: "600",
                          fontFamily: "Inter",
                          color: "#121828",
                        }}
                      >
                        {parsedData?.articles?.updated?.overall ?? "N/A"}
                        {parsedData?.articles?.updated?.overall !== undefined &&
                          parsedData?.articles?.original?.overall !==
                            undefined &&
                          parsedData.articles.updated.overall >
                            parsedData.articles.original.overall && (
                            <span
                              style={{
                                color: "#4caf50",
                                fontSize: "12px",
                                marginLeft: "5px",
                                marginBottom: "25px",
                              }}
                            >
                              +
                              {(
                                parsedData.articles.updated.overall -
                                parsedData.articles.original.overall
                              ).toFixed(1)}
                              ▲
                            </span>
                          )}
                        {parsedData?.articles?.updated?.overall !== undefined &&
                          parsedData?.articles?.original?.overall !==
                            undefined &&
                          parsedData.articles.updated.overall <
                            parsedData.articles.original.overall && (
                            <span
                              style={{
                                color: "#F44336",
                                fontSize: "12px",
                                marginLeft: "5px",
                                marginBottom: "25px",
                              }}
                            >
                              {(
                                parsedData.articles.updated.overall -
                                parsedData.articles.original.overall
                              ).toFixed(1)}
                              ▼
                            </span>
                          )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            ) : isEEATMode ? (
              <Card
                sx={{
                  p: 3,
                  borderRadius: "12px",
                  boxShadow: "none",
                  border: "1px solid #E6E8F0",
                  mb: 2,
                  textAlign: "left",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "18px",
                    fontFamily: "Inter",
                    color: "#121828",
                    mb: 1,
                  }}
                >
                  Overall EEAT Score
                </Typography>
                <Typography
                  sx={{
                    color: "#7B85AD",
                    fontSize: "15px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    mb: 2,
                  }}
                >
                  Here’s how your article stacks up on Experience, Expertise,
                  Authority, and Trust.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "left",
                    justifyContent: "left",
                    mb: "2px",
                    mt: 2,
                  }}
                >
                  <Box
                    sx={{
                      background: "#F9FAFC",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "left",
                      gap: 1,
                      width: "fit-content",
                      // mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#7B85AD",
                        fontWeight: 600,
                        fontSize: "15px",
                        mr: 2,
                      }}
                    >
                      Original
                    </Typography>
                    <Box display="flex" flexDirection="row" gap="8px">
                      <ScoreCircle
                        score={singleArticleData?.overall}
                        size={24}
                      />

                      <Typography
                        sx={{
                          color: "#7B85AD",
                          fontWeight: 600,
                          fontSize: "18px",
                          // ml: 2,
                        }}
                      >
                        {singleArticleData?.overall ?? "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  className="relative"
                  color="success"
                  sx={{
                    mt: 4,
                    mr: 2,
                    backgroundImage:
                      "linear-gradient(86.04deg,#FFD875 -18.56%, #DA00C8 35.8%, #6A00FF 106.2%)",
                  }}
                  size="large"
                  onClick={onUpdateArticleEEAT}
                >
                  {isEEATLoading ? (
                    <CircularProgress size={24} sx={{ color: "#fff", mr: 2 }} />
                  ) : (
                    "Update Content to Boost EEAT"
                  )}
                </Button>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 1,
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{ color: "#919EAB", fontSize: 18, mr: 1 }}
                  />
                  <Typography
                    sx={{
                      color: "#919EAB",
                      fontSize: "14px",
                      fontFamily: "Inter",
                      fontWeight: 400,
                    }}
                  >
                    Get an instant content update. Our AI reviews search intent,
                    fills in missing info, removes the fluff, and delivers an
                    article that scores higher on EEAT.
                  </Typography>
                </Box>
              </Card>
            ) : (
              // Single article mode without Card wrapper
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                padding="16px 24px"
                width="100%"
                sx={{
                  borderRadius: "8px",
                  background: "#F9FAFC",
                  border: "1px solid #E6E8F0",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    fontFamily: "Inter",
                    color: "#121828",
                  }}
                >
                  Overall EEAT
                </Typography>

                <Box display="flex" alignItems="center" gap="12px">
                  <ScoreCircle score={singleArticleData?.overall} size={32} />
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: "600",
                      fontFamily: "Inter",
                      color: "#121828",
                    }}
                  >
                    {singleArticleData?.overall?.toFixed(1) ?? "N/A"}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Tabs */}
          <Box
            sx={{
              mb: 3,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{ "& .MuiTabs-indicator": { backgroundColor: "#5048E5" } }}
            >
              <Tab
                icon={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        color: tabValue === 0 ? "#5048E5" : "text.secondary",
                      }}
                    >
                      <ComparisonIcon
                        sx={{
                          width: 25,
                          height: 25,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 500, mb: "11px" }}
                    >
                      {optimizedMode ? "Comparison" : "Assessment"}
                    </Typography>
                  </Box>
                }
                sx={{
                  color: tabValue === 0 ? "#5048E5" : "text.secondary",
                  fontWeight: 500,
                  marginRight: "auto",
                  width: "50%",
                  minHeight: "48px",
                  padding: "12px 16px",
                }}
              />
              <Tab
                icon={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <Box
                      sx={{
                        color: tabValue === 1 ? "#5048E5" : "text.secondary",
                      }}
                    >
                      <LightBulb
                        sx={{
                          width: 20,
                          height: 20,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 500, mb: "8px" }}
                    >
                      Suggestions
                    </Typography>
                  </Box>
                }
                sx={{
                  color: tabValue === 1 ? "#5048E5" : "text.secondary",
                  fontWeight: 500,
                  marginLeft: "auto",
                  width: "50%",
                  minHeight: "48px",
                  padding: "12px 16px",
                }}
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box hidden={tabValue !== 0}>
            {optimizedMode ? (
              // Comparison mode
              <>
                <ScoreDetail
                  label="Experience"
                  original={
                    parsedData?.articles?.original?.scores?.experience ?? 0
                  }
                  updated={
                    parsedData?.articles?.updated?.scores?.experience ?? 0
                  }
                  description={analysis?.experience?.comparison || ""}
                />
                <ScoreDetail
                  label="Expertise"
                  original={
                    parsedData?.articles?.original?.scores?.expertise ?? 0
                  }
                  updated={
                    parsedData?.articles?.updated?.scores?.expertise ?? 0
                  }
                  description={analysis?.expertise?.comparison || ""}
                />
                <ScoreDetail
                  label="Authoritativeness"
                  original={
                    parsedData?.articles?.original?.scores?.authoritativeness ??
                    0
                  }
                  updated={
                    parsedData?.articles?.updated?.scores?.authoritativeness ??
                    0
                  }
                  description={analysis?.authoritativeness?.comparison || ""}
                />
                <ScoreDetail
                  label="Trustworthiness"
                  original={
                    parsedData?.articles?.original?.scores?.trustworthiness ?? 0
                  }
                  updated={
                    parsedData?.articles?.updated?.scores?.trustworthiness ?? 0
                  }
                  description={analysis?.trustworthiness?.comparison || ""}
                />
              </>
            ) : (
              // Single article mode
              <>
                <SingleScoreDetail
                  label="Experience"
                  score={singleArticleData?.scores?.experience ?? 0}
                  assessment={
                    analysis?.experience?.comparison ||
                    analysis?.experience?.assessment ||
                    ""
                  }
                />
                <SingleScoreDetail
                  label="Expertise"
                  score={singleArticleData?.scores?.expertise ?? 0}
                  assessment={
                    analysis?.expertise?.comparison ||
                    analysis?.expertise?.assessment ||
                    ""
                  }
                />
                <SingleScoreDetail
                  label="Authoritativeness"
                  score={singleArticleData?.scores?.authoritativeness ?? 0}
                  assessment={
                    analysis?.authoritativeness?.comparison ||
                    analysis?.authoritativeness?.assessment ||
                    ""
                  }
                />
                <SingleScoreDetail
                  label="Trustworthiness"
                  score={singleArticleData?.scores?.trustworthiness ?? 0}
                  assessment={
                    analysis?.trustworthiness?.comparison ||
                    analysis?.trustworthiness?.assessment ||
                    ""
                  }
                />
              </>
            )}
          </Box>

          {/* Suggestions Tab Content */}
          <Box hidden={tabValue !== 1}>
            <SuggestionsList suggestions={suggestions} />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            py: 2,
            px: 1,
          }}
        >
          {/* Icon for visual appeal */}
          <Box sx={{ mb: 3, mt: 1, color: "#5048E5" }}>
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 16V12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 8H12.01"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontSize: "18px",
              fontWeight: "600",
              fontFamily: "Inter",
              color: "#121828",
              textAlign: "center",
              mb: 2,
            }}
          >
            EEAT Score Not Available
          </Typography>

          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: "400",
              fontFamily: "Inter",
              color: "#121828",
              textAlign: "center",
              mb: 3,
            }}
          >
            EEAT scores are only available for newly created or recently updated
            articles.
          </Typography>

          <Card
            sx={{ width: "100%", mb: 2, p: 2, border: "1px solid #E6E8F0" }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                fontFamily: "Inter",
                color: "#121828",
                mb: 1,
              }}
            >
              What is EEAT?
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "400",
                fontFamily: "Inter",
                color: "#121828",
                mb: 2,
              }}
            >
              Google's EEAT stands for Experience, Expertise, Authoritativeness,
              and Trustworthiness - key factors that determine content quality.
            </Typography>

            <Box sx={{ mt: 2 }}>
              {[
                {
                  title: "Experience",
                  desc: "First-hand experience with the topic",
                },
                {
                  title: "Expertise",
                  desc: "Knowledge and skills in the subject area",
                },
                {
                  title: "Authoritativeness",
                  desc: "Reputation for accurate information",
                },
                {
                  title: "Trustworthiness",
                  desc: "Content reliability and credibility",
                },
              ].map((item, index) => (
                <Box key={index} sx={{ display: "flex", mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: "#5048E5",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1.5,
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    {item.title.charAt(0)}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#121828",
                        lineHeight: 1.2,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#6B7280",
                        lineHeight: 1.2,
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        </Box>
      )}

      {hasData &&
        summary &&
        summary.length > 0 &&
        tabValue === 0 &&
        !isLoading && (
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
            }}
          >
            <Button
              startIcon={
                <Box
                  component="span"
                  sx={{ display: "flex", alignItems: "center", mr: "-8px" }}
                >
                  {showDetailedSummary ? <MinusOutlined /> : <LightBulb />}
                </Box>
              }
              onClick={() => setShowDetailedSummary(!showDetailedSummary)}
              sx={{
                color: "#5048E5",
                fontWeight: 600,
                fontSize: "14px",
                textTransform: "none",
                fontFamily: "Inter",
              }}
            >
              {showDetailedSummary ? "Hide" : "Show"} Detailed{" "}
              {optimizedMode ? "Change " : ""}Summary
            </Button>
          </Box>
        )}
      {isLoading && (
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="text" height={30} width="60%" />
          {optimizedMode && <Skeleton variant="text" height={24} width="80%" />}
        </Box>
      )}

      {/* Detailed Summary Section */}
      {showDetailedSummary &&
        isDataComplete &&
        tabValue === 0 &&
        summary &&
        summary.length > 0 && (
          <Box ref={detailedSummaryRef}>
            <DetailedSummary summary={summary} optimizedMode={optimizedMode} />
          </Box>
        )}
    </Box>
  );
};

export default Sidebar;
